import { useNavigate, useParams } from 'react-router-dom';
import { Button, Select } from 'antd';
import { ArrowLeft, Printer, Download } from 'lucide-react';
import { useBreakpoint } from '@/hooks/useBreakpoint';

export function PrintPreviewPage() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const handlePrint = () => window.print();

  return (
    <div style={{ minHeight: '100vh', background: '#eceef1', padding: isMobile ? 12 : 24 }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
        }
      `}</style>

      {/* Toolbar */}
      <div
        className="no-print"
        style={{
          maxWidth: 960,
          margin: '0 auto 16px',
          background: '#fff',
          borderRadius: 8,
          padding: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          border: '1px solid #d4d9df',
        }}
      >
        <Button icon={<ArrowLeft size={14} />} onClick={() => navigate(-1)} size={isMobile ? 'small' : 'middle'}>
          {isMobile ? '' : 'Đóng'}
        </Button>
        <div style={{ flex: 1, textAlign: 'center', fontWeight: 500, fontSize: isMobile ? 12 : 14, minWidth: 120 }}>
          {isMobile ? docId ?? 'GV-2026-0412' : `Phiếu giao việc · ${docId ?? 'GV-2026-0412'}`}
        </div>
        {!isMobile && (
          <Select defaultValue="a4-portrait" size="small" style={{ width: 140 }} options={[
            { value: 'a4-portrait', label: 'A4 · Dọc' }, { value: 'a4-landscape', label: 'A4 · Ngang' },
          ]} />
        )}
        <Button icon={<Download size={14} />} size={isMobile ? 'small' : 'middle'}>PDF</Button>
        <Button type="primary" icon={<Printer size={14} />} onClick={handlePrint} size={isMobile ? 'small' : 'middle'}>In</Button>
      </div>

      {/* Paper */}
      <div
        style={{
          maxWidth: 960,
          margin: '0 auto',
          background: '#fff',
          aspectRatio: 0.707,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          borderRadius: 8,
          padding: isMobile ? 24 : 60,
          display: 'flex',
          flexDirection: 'column',
          fontSize: isMobile ? 10 : 13,
          color: '#1a1e23',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            borderBottom: '1px solid #adb6c0',
            paddingBottom: 16,
            marginBottom: 24,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 56,
                height: 56,
                background: '#6699CC',
                color: '#fff',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: 18,
              }}
            >
              HV
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>BỆNH VIỆN HƯNG VIỆT</div>
              <div style={{ fontSize: 11, color: '#7d8a98' }}>Số 34 Đại Cồ Việt, Hai Bà Trưng, Hà Nội</div>
              <div style={{ fontSize: 11, color: '#7d8a98' }}>ĐT: (024) xxx xxxx · Email: info@hungviet.vn</div>
            </div>
          </div>
          <div style={{ textAlign: 'center', fontSize: 11 }}>
            <div>CỘNG HOÀ XÃ HỘI CHỦ NGHĨA VIỆT NAM</div>
            <div style={{ fontStyle: 'italic' }}>Độc lập - Tự do - Hạnh phúc</div>
            <div style={{ marginTop: 12 }}>Hà Nội, ngày 18 tháng 4 năm 2026</div>
          </div>
        </div>

        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 20 }}>PHIẾU GIAO VIỆC</div>
          <div className="mono" style={{ fontSize: 12, color: '#7d8a98', marginTop: 4 }}>
            Số: {docId ?? 'GV-2026-0412'}
          </div>
        </div>

        {/* Info */}
        <table style={{ width: '100%', fontSize: 12, marginBottom: 20 }}>
          <tbody>
            {[
              ['Người giao việc:', 'Nguyễn Văn A - Giám đốc Bệnh viện'],
              ['Người nhận việc:', 'Phạm Ngọc Ánh - Trưởng phòng CNTT'],
              ['Đơn vị:', 'Phòng Công nghệ Thông tin'],
              ['Mã công việc:', 'TSK-0341'],
              ['Ngày giao:', '15/04/2026'],
              ['Hạn hoàn thành:', '18/04/2026 (17:00)'],
              ['Độ ưu tiên:', 'Cao'],
            ].map(([k, v]) => (
              <tr key={k}>
                <td style={{ fontWeight: 600, width: 160, padding: '4px 0' }}>{k}</td>
                <td>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Content */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Nội dung công việc:</div>
          <div style={{ lineHeight: 1.7 }}>
            Hoàn thiện kế hoạch triển khai hệ thống SIEM Wazuh + ELK cho toàn bộ hệ thống HIS (DigiHC/smartHos365) trong quý 2 năm 2026, bao gồm:
          </div>
          <ul style={{ paddingLeft: 20, marginTop: 6, lineHeight: 1.8 }}>
            <li>Khảo sát và lập danh sách 10 nguồn log cần giám sát</li>
            <li>Cài đặt Wazuh Manager server trung tâm</li>
            <li>Cấu hình 8 use case phát hiện mối đe dọa</li>
            <li>Xây dựng quy trình xử lý sự cố bảo mật</li>
            <li>Đào tạo vận hành cho đội ngũ CNTT</li>
          </ul>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Yêu cầu kết quả:</div>
          <div style={{ lineHeight: 1.7 }}>
            Báo cáo kế hoạch chi tiết, bảng ma trận log sources, tài liệu quy trình IRP, slide đào tạo.
          </div>
        </div>

        {/* Signatures */}
        <div style={{ marginTop: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, paddingTop: 20 }}>
          {[
            { title: 'NGƯỜI NHẬN VIỆC', name: 'Phạm Ngọc Ánh', sig: 'Ánh', time: '15/04/2026 09:45', code: 'SIG-20260415-B2D8' },
            { title: 'NGƯỜI GIAO VIỆC', name: 'Nguyễn Văn A', sig: 'N.V.A', time: '15/04/2026 09:30', code: 'SIG-20260415-A7F1' },
          ].map((s) => (
            <div key={s.title} style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 600 }}>{s.title}</div>
              <div style={{ fontSize: 11, fontStyle: 'italic', margin: '4px 0 20px' }}>(Ký, ghi rõ họ tên)</div>
              <div style={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ color: '#6699CC', fontFamily: 'Brush Script MT,cursive', fontSize: 26, fontStyle: 'italic' }}>
                  {s.sig}
                </div>
              </div>
              <div style={{ fontWeight: 600 }}>{s.name}</div>
              <div className="mono" style={{ fontSize: 10, color: '#7d8a98', marginTop: 4 }}>✓ Ký điện tử · {s.time}</div>
              <div className="mono" style={{ fontSize: 10, color: '#adb6c0' }}>{s.code}</div>
            </div>
          ))}
        </div>

        {/* QR footer */}
        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            borderTop: '1px solid #eceef1',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 10,
            color: '#7d8a98',
          }}
        >
          <div>
            <div>Xác thực tài liệu tại: https://task.hungviet.vn/v/{docId ?? 'GV-2026-0412'}</div>
            <div className="mono">Hash SHA-256: e3b0c44298fc1c149afbf4c8996fb924...</div>
          </div>
          <div
            style={{
              width: 60,
              height: 60,
              background: 'repeating-conic-gradient(#000 0 25%, #fff 0 50%) 50% / 10px 10px',
              border: '1px solid #1a1e23',
            }}
          />
        </div>
      </div>
    </div>
  );
}
